import type { UrlLogic, UrlVariant } from '../types';

/**
 * Pure post-quiz destination resolution, extracted from the two historical
 * copies (store.redirectToLanding and Gate.vue setLead) so the legacy paths
 * and the flow engine share ONE implementation of the money routing.
 *
 * Resolution algorithm (behavior-pinned by redirect-resolution.spec.ts —
 * any change here must keep those regression tests green):
 *   1. Rules are evaluated in ascending `priority` order (undefined priority
 *      sorts last). The input array is NOT mutated — both historical copies
 *      sorted `quizData.url_logic` in place, which silently reordered store
 *      state; this function sorts a copy.
 *   2. A rule matches when it has no answers, an empty answers list, or every
 *      recorded lead answer value is contained in the rule's answer values.
 *   3. On match, the rule's url wins, then variant selection may replace it:
 *      - legacy `url_variant` + `variant_weight` (0<w<100): weighted coin flip
 *      - `url_variants` as string[]: uniform random pick
 *      - `url_variants` as {url, weight}[]: cumulative-weight pick with the
 *        remainder (up to 100) falling back to the rule url; total weight
 *        > 100 falls back to the rule url
 *   4. No matching rule → undefined (callers fall back to quiz_default_url).
 *
 * `rng` is injectable so the weighted branches are deterministic under test.
 */
export function resolveRedirectUrl(
  urlLogic: UrlLogic[] | undefined,
  leadAnswerValues: string[],
  rng: () => number = Math.random
): string | undefined {
  const rules = [...(urlLogic || [])].sort((a, b) => {
    if (a.priority === undefined && b.priority === undefined) return 0;
    if (a.priority === undefined) return 1;
    if (b.priority === undefined) return -1;
    return a.priority - b.priority;
  });

  for (const rule of rules) {
    const ruleAnswerValues = (rule.answers || []).map((answer) => answer.value);
    // A conditional rule needs at least one recorded answer to match: with
    // none, `[].every()` is vacuously true and the first rule by priority
    // would win. Legacy never reached this (the gate sits behind
    // allQuestionsAnswered) but the flow engine's form-gate skip does — a
    // skipper with no selections must land on quiz_default_url.
    const matches =
      !rule.answers ||
      rule.answers.length === 0 ||
      (leadAnswerValues.length > 0 &&
        leadAnswerValues.every((answer) => ruleAnswerValues.includes(answer)));
    if (!matches) continue;

    let direct_to: string | undefined = rule.url;

    if (
      rule.url_variant &&
      rule.variant_weight &&
      Number(rule.variant_weight) > 0 &&
      Number(rule.variant_weight) < 100
    ) {
      if (rng() >= 1 - parseInt(rule.variant_weight.toString()) / 100) {
        direct_to = rule.url_variant;
      }
    } else if (rule.url_variants) {
      if (
        Array.isArray(rule.url_variants) &&
        rule.url_variants.length > 0 &&
        typeof rule.url_variants[0] === 'string'
      ) {
        const stringVariants = rule.url_variants.filter(
          (v): v is string => typeof v === 'string'
        );
        if (stringVariants.length > 0) {
          return stringVariants[Math.floor(rng() * stringVariants.length)];
        }
      }

      const weightedVariants = (rule.url_variants as (UrlVariant | string)[]).filter(
        (v): v is UrlVariant =>
          typeof v === 'object' && v !== null && 'weight' in v && 'url' in v
      );
      const totalVariantWeight = weightedVariants.reduce(
        (sum, variant) => sum + variant.weight,
        0
      );

      if (totalVariantWeight > 100) {
        console.error('Variant weights exceed 100%. Using default URL.');
        return rule.url;
      }
      if (weightedVariants.length === 0) return rule.url;

      let cumulativeWeight = 0;
      const variantWeights: number[] = [];
      weightedVariants.forEach((variant) => {
        cumulativeWeight += variant.weight / 100;
        variantWeights.push(cumulativeWeight);
      });
      variantWeights.push(1);

      const randomNum = rng();
      for (let i = 0; i < variantWeights.length; i++) {
        if (randomNum < variantWeights[i]) {
          return i < weightedVariants.length ? weightedVariants[i].url : rule.url;
        }
      }
    }

    if (direct_to) return direct_to;
  }

  return undefined;
}
